import React, { useEffect, useRef, useState } from 'react';
import ProviderIdentityBulkReview from './ProviderIdentityBulkReview';
import { applyProviderIdentityReviews, previewProviderIdentityReviews } from '../utils/ApiCall';
import { normalizeWatchlistLifecycle } from '../utils/watchlistLifecycle';
import { parseWatchlistPublicationReceipt } from '../utils/watchlistPublication';
import { selectFactorVendorReviews } from '../utils/factorReviewVisibility';
import {
    dispatchMsiSecurityResolutionAppliedEvent,
    parseWatchlistDeltaClassification,
    WATCHLIST_CLIENT_PUBLICATION_REQUIRED_FIELD,
    WATCHLIST_REVISION_PROGRESS_EVENT,
    scopeSecurityOperationEventDetail,
    WATCHLIST_REVISION_PROGRESS_OBSERVED_EVENT,
    isSecurityOperationEventForScope,
} from '../utils/securityResolutionEvents';

export default function ProviderIdentityReviewSection({
    groups, referenceReviews = [], selectedFactorVendor, expectedRevisionId, pendingRevisionId, sessionScopeToken, onRefresh,
}) {
    const vendor = String(selectedFactorVendor || '').trim().toLowerCase();
    const scope = `${sessionScopeToken || ''}:${vendor}`;
    const currentSession = useRef(sessionScopeToken);
    currentSession.current = sessionScopeToken;
    const [localPending, setLocalPending] = useState(null);
    const pending = pendingRevisionId || (localPending?.scope === scope ? localPending.revisionId : null);
    const pendingRef = useRef(pending);
    pendingRef.current = pending;
    useEffect(() => {
        if (localPending && expectedRevisionId !== localPending.baseRevisionId) setLocalPending(null);
    }, [expectedRevisionId, localPending]);
    useEffect(() => {
        const observed = ({ detail }) => {
            if (!isSecurityOperationEventForScope(detail, sessionScopeToken)
                || detail?.watchlist_revision_id !== pendingRef.current) return;
            const status = detail.lifecycle?.status || detail.status;
            if (['succeeded', 'unchanged', 'failed', 'cancelled', 'stale', 'superseded'].includes(status)) {
                pendingRef.current = null;
                setLocalPending(null);
                Promise.resolve(onRefresh?.({ force: true })).catch(() => {});
            }
        };
        window.addEventListener(WATCHLIST_REVISION_PROGRESS_OBSERVED_EVENT, observed);
        return () => window.removeEventListener(WATCHLIST_REVISION_PROGRESS_OBSERVED_EVENT, observed);
    }, [sessionScopeToken, onRefresh]);
    const { groups: visibleGroups, reviews: visibleReferences } = selectFactorVendorReviews(
        { groups, reviews: referenceReviews }, vendor,
    );
    const tableGroups = [...visibleGroups, ...visibleReferences.map((review) => ({
        security_key: `reference:${review.review_key}`,
        current_security: review.current_security,
        coverage: { state: 'unverified' },
        proposals: (review.options || []).map((option, index) => ({
            proposal_key: `reference:${review.review_key}:${index}`,
            provider: review.provider,
            listing_security: { ...option, name: option.security_name },
            provider_security: review.provider_security,
            reference_only: true,
            action_available: false,
            guidance: 'Review the country and listing choices in Security Resolution.',
        })),
    }))];
    const preview = async (selections, attemptId) => {
        const response = await previewProviderIdentityReviews({
            selections, attemptId, expectedWatchlistRevisionId: expectedRevisionId,
        });
        const hasRemoval = selections.some((selection) => selection.action === 'remove');
        const removalImpacts = response?.removal_impacts;
        const completeRemovalImpact = Array.isArray(removalImpacts)
            && removalImpacts.length > 0
            && removalImpacts.length === response?.summary?.removed_count
            && removalImpacts.every((security) => security?.security_key && typeof security.in_actual_portfolio === 'boolean')
            && new Set(removalImpacts.map((security) => security.security_key)).size === removalImpacts.length
            && removalImpacts.filter((security) => security.in_actual_portfolio).length === response?.summary?.portfolio_removed_count;
        if (!response?.preview_token || !response?.expected_watchlist_revision_id
            || !Array.isArray(response.changes)
            || response.changes.length !== selections.length
            || !Number.isSafeInteger(response.summary?.portfolio_removed_count)
            || response.summary.portfolio_removed_count < 0
            || (hasRemoval && !completeRemovalImpact)) {
            throw new Error(response?.message || 'The review could not be prepared. Your selections have been kept.');
        }
        return response;
    };
    const apply = async (review, attemptId) => {
        const response = await applyProviderIdentityReviews({
            selections: review.decisions,
            attemptId,
            expectedWatchlistRevisionId: review.expected_watchlist_revision_id,
            previewToken: review.preview_token,
        });
        if (response?.success !== true) {
            throw new Error(response?.message || 'The review could not be applied. Your selections have been kept.');
        }
        // A vendor switch resets local choices, but a committed successor still
        // belongs to this session and must advance the application's revision.
        if (currentSession.current !== sessionScopeToken) return response;
        // A saved review can publish a successor without changing coverage.
        // The app shell must still advance its revision fence.
        const publishedRevisionId = response.watchlist_revision_id
            || response.lifecycle?.watchlist_revision_id;
        const admissionLifecycle = response.requires_admission ? normalizeWatchlistLifecycle(response) : null;
        if (['queued', 'running'].includes(admissionLifecycle?.status)) {
            const lifecycle = admissionLifecycle;
            const receipt = parseWatchlistPublicationReceipt(lifecycle);
            if (!receipt) throw new Error('The review returned an incomplete admission receipt. Retry these same choices.');
            const detail = scopeSecurityOperationEventDetail({
                lifecycle, status: lifecycle.status, phase: lifecycle.phase,
                operation_id: lifecycle.operation_id,
                watchlist_revision_id: receipt.revisionId, revision_seq: receipt.revisionSeq,
                operation_type: 'watchlist_update',
                [WATCHLIST_CLIENT_PUBLICATION_REQUIRED_FIELD]: true,
            }, sessionScopeToken);
            pendingRef.current = receipt.revisionId;
            setLocalPending({ scope, revisionId: receipt.revisionId, baseRevisionId: expectedRevisionId });
            if (detail) window.dispatchEvent(new CustomEvent(WATCHLIST_REVISION_PROGRESS_EVENT, { detail }));
            try { await onRefresh?.({ force: true }); } catch (_error) { /* Durable processing continues. */ }
            return response;
        }
        const hasSuccessor = publishedRevisionId
            && publishedRevisionId !== review.expected_watchlist_revision_id;
        if (hasSuccessor || response.watchlist_changed !== false || response.factor_data_changed === true) {
            const lifecycle = normalizeWatchlistLifecycle(response);
            const receipt = parseWatchlistPublicationReceipt(lifecycle);
            if (!receipt || !['succeeded', 'unchanged'].includes(lifecycle.status)) {
                throw new Error('The review returned an incomplete publication receipt. Retry these same choices.');
            }
            const classification = parseWatchlistDeltaClassification(response);
            if (classification.error) throw new Error(classification.error);
            const detail = {
                lifecycle,
                status: lifecycle.status,
                phase: lifecycle.phase,
                operation_id: lifecycle.operation_id,
                operation_type: response.operation_type,
                watchlist_revision_id: receipt.revisionId,
                revision_seq: receipt.revisionSeq,
                [WATCHLIST_CLIENT_PUBLICATION_REQUIRED_FIELD]: true,
                delta_classification: classification.classification,
                watchlist_mutation_delta: response.watchlist_mutation_delta,
            };
            dispatchMsiSecurityResolutionAppliedEvent(detail, sessionScopeToken);
        }
        try {
            await onRefresh?.({ force: true });
        } catch (_error) {
            // A refresh failure must not turn a successful publication into a
            // retryable mutation. The settings container owns subsequent reads.
            return { ...response, refreshWarning: 'Your choices were applied. Refresh Security Resolution to see the latest status.' };
        }
        return response;
    };
    return <ProviderIdentityBulkReview
        key={scope}
        groups={tableGroups}
        selectedFactorVendor={vendor}
        expectedRevisionId={expectedRevisionId}
        disabled={!sessionScopeToken || !expectedRevisionId || Boolean(pending)}
        processing={Boolean(pending)}
        onPreview={preview}
        onApply={apply}
        onRefresh={onRefresh ? () => onRefresh({ force: true }) : undefined}
    />;
}
